import { _ } from "../../core/polyglot/polyglot.js";
import { formatAsShortDate } from "../../core/helpers/date-formatting-util.js";
import { formatForceTwoDecimals } from "../../core/helpers/count-formatting-util.js";
import { formatFullWithTwoDecimals } from "../../core/helpers/currency-formatting-util.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import AccountStatement from "../../models/AccountStatement.js";
import InlineDimensionLabelsWidget from "../../core/widgets/InlineDimensionLabelsWidget.js";
import linkedDocumentService from "./linkedDocumentService.js";
import notifier from "../../core/widgets/notifier/notifier.js";
import session from "../../core/session.js";
import specificationFactory from "../../models/specification/specificationFactory.js";
import state from "../../core/state.js";
import windowedDocument from "./windowedDocument.js";

/**
 * Displays an account statement table for an account between specified dates.
 */
function accountStatementDocument(spec, my) {
	spec ||= {};
	my ||= {};

	const that = windowedDocument(spec, my);

	my.repository = makeMonitorRepository({
		route: "/Accounting2/Company/#companyId/AccountStatement",
		model: AccountStatement,
		defaultFilters: {
			companyId: session.getCompany().getLegacyId(),
		},
		antiForgeryToken: session.getAntiForgeryToken(),
	});

	my.state = state.empty;

	that.register("stateChanged", () => {
		that.update();
	});

	that.headerTitle = _("Account statement");
	that.title = _("Account statement");

	that.hasPeriodPicker = function () {
		return false;
	};

	/**
	 * @param {string} options.accountNumber - id of the account
	 * @param {string} options.dimensionIdentifier - id of the dimension
	 */
	that.showAccountStatement = async function (options) {
		my.period = session.getPeriod();
		my.accountNumber = options.accountNumber;

		let dimensionSpec;

		if (options.dimensionSpecification) {
			const dimensionObject = JSON.parse(
				decodeURIComponent(options.dimensionSpecification),
			);

			dimensionSpec = specificationFactory.materialize(dimensionObject);
		}

		that.showUpdating();

		my.accountStatement = await my.repository.save({
			filter: {
				accountNumber: my.accountNumber,
				periodString: my.period.toString(),
			},
			model: dimensionSpec,
			notify: false, // remove useless success message
		});

		my.dimensionSpec = dimensionSpec;
		that.showContent();
	};

	my.renderContentOn = function (html) {
		html.div(
			{ class: "panel-body panel-body-white" },
			html.div(
				{ class: "panel-title" },
				html.h2(
					{
						class: "account-statement-title",
						"data-test": "account-statement-title",
					},
					my.period.toSmartString(),
				),
				html.p(
					{
						class: "subtitle",
						"data-test": "account-statement-subtitle",
					},
					_("Account: {number} - {name}", {
						number: my.accountNumber,
						name: my.accountStatement.getAccount().getName(),
					}),
				),
				new InlineDimensionLabelsWidget({
					prefix: _("Account statement filtered on"),
					dimensionTree: session.getDimensionTree(),
					dimensionSpecification: my.dimensionSpec,
				}),
			),
		);

		my.renderTableOn(html);
	};

	my.renderTableOn = function (html) {
		html.table(
			{
				klass: "table table-auto table-striped table-condensed table-small table-hover",
			},
			html.thead(renderHeaderOn, renderOpeningOn),
			html.tbody({ class: "table-condensed" }, renderItemsOn),
			html.tfoot(renderClosingOn, renderTotalOn),
		);
	};

	function renderHeaderOn(html) {
		const headers = [
			_("Ledger no."),
			_("Date"),
			_("Text"),
			{
				name: _("Debit"),
				class: "text-right",
			},
			{
				name: _("Credit"),
				class: "text-right",
			},
			{
				name: _("Balance"),
				class: "text-right",
			},
		];

		if (my.accountStatement.haveDimensions()) {
			headers.splice(3, 0, _("Dimension"));
		}

		html.tr(
			headers.map((header) => {
				if (header.name) {
					return html.th(header, header.name);
				}
				return html.th(header);
			}),
		);
	}

	function renderOpeningOn(html) {
		html.tr(
			{ class: "opening" },
			html.td(),
			html.td(dateRenderer(my.accountStatement.getTimespan().getStart())),
			html.td(_("Opening balance")),
			optionalDimensionColumnRenderer(_("Name")),
			html.td(amountRenderer(my.accountStatement.getOpeningDebit())),
			html.td(
				amountRenderer(my.accountStatement.getReversedOpeningCredit()),
			),
			html.td(amountRenderer(my.accountStatement.getOpeningBalance())),
		);
	}

	function renderItemsOn(html) {
		my.accountStatement.getItems().map((item) =>
			html.tr(
				html.td(
					html.a(item.getLedgerEntry().getUniqueIdentifier()).href(
						my.linkTo("ledger-entry", {
							fiscalYearId: item
								.getLedgerEntry()
								.getFiscalYearId(),
							ledgerId: item
								.getLedgerEntry()
								.getUniqueIdentifier(),
						}),
					),
					linkedDocumentRenderer(item.getLedgerEntry()),
				),
				html.td(dateRenderer(item.getDate())),
				html.td(item.getText()),
				optionalDimensionColumnRenderer(
					item
						.getDimensions()
						.map((dimension) => dimension.getName())
						.join(", "),
				),
				html.td(amountRenderer(item.getDebit())),
				html.td(amountRenderer(item.getReversedCredit())),
				html.td(formattedAmountRenderer(item.getBalance())),
			),
		);
	}

	function renderClosingOn(html) {
		html.tr(
			{ class: "closing" },
			html.td(),
			html.td(
				{ class: "bold important" },
				dateRenderer(my.accountStatement.getTimespan().getEnd()),
			),
			html.td({ class: "bold" }, _("Closing balance")),
			optionalDimensionColumnRenderer(""),
			html.td(amountRenderer(my.accountStatement.getSumDebit())),
			html.td(amountRenderer(my.accountStatement.getReversedSumCredit())),
			html.td(
				{ class: "bold important" },
				amountRenderer(my.accountStatement.getClosingBalance()),
			),
		);
	}

	function renderTotalOn(html) {
		html.tr(
			{ class: "total" },
			html.td(
				{ colspan: "2" },
				html.div(
					{ class: "text-justify text-center bold important" },
					dateRenderer(my.accountStatement.getTimespan().getStart()),
					" - ",
					dateRenderer(my.accountStatement.getTimespan().getEnd()),
				),
			),
			html.td(""),
			optionalDimensionColumnRenderer(""),
			html.td(
				amountRenderer(
					my.accountStatement.getSumDebit() -
						my.accountStatement.getOpeningDebit(),
				),
			),
			html.td(
				amountRenderer(
					my.accountStatement.getReversedSumCredit() -
						my.accountStatement.getReversedOpeningCredit(),
				),
			),
			html.td(
				{ class: "bold important" },
				amountRenderer(
					my.accountStatement.getClosingBalance() -
						my.accountStatement.getOpeningBalance(),
				),
			),
		);
	}

	function linkedDocumentRenderer(ledgerEntry) {
		return function (html) {
			if (!ledgerEntry.hasLinkedDocument()) {
				return;
			}
			if (linkedDocumentService.haveLinkedDocumentIntegration()) {
				html.a(
					{
						class: "pull-right",
						href: linkedDocumentService.getByLedgerEntry(
							ledgerEntry,
						),
						target: "_blank",
					},
					html.em({ class: "fp-icon advisory-report-icon" }),
				);
			} else {
				html.a(
					{ class: "pull-right" },
					html.em({ class: "fp-icon advisory-report-icon" }),
				).click(() => {
					notifier.error(
						_(
							"Please re-authenticate by using the import wizard or contact your local support.",
						),
					);
				});
			}
		};
	}

	function optionalDimensionColumnRenderer(columnItem) {
		return function (html) {
			if (!my.accountStatement.haveDimensions()) {
				return;
			}
			html.td(columnItem);
		};
	}

	function dateRenderer(aDate) {
		return function (html) {
			html.span().renderNonBreaking(formatAsShortDate(aDate));
		};
	}

	function amountRenderer(amount) {
		if (amount) {
			return formattedAmountRenderer(amount);
		}
		return "";
	}

	function formattedAmountRenderer(amount) {
		return function (html) {
			html.span({ class: "pull-right" }).renderNonBreaking(
				amount === 0
					? formatForceTwoDecimals(amount)
					: formatFullWithTwoDecimals(amount),
			);
		};
	}

	return that;
}

export default accountStatementDocument;
